import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, ChevronRight, Link2 } from 'lucide-react';
import { format } from 'date-fns';
import { demoRequestService } from '../../services/api';
import { DEMO_STATUSES, DEPLOYMENTS, statusLabel, statusClass, deploymentLabel } from './demoStatus';
import '../Tickets.css';

// Internal list of product demo requests submitted from the company website.
export default function Demos() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ status: '', deployment: '' });
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const params = { page, limit: 20, ...filters, search };
    Object.keys(params).forEach(k => !params[k] && delete params[k]);
    const res = await demoRequestService.getAll(params);
    setItems(res.data.items); setTotal(res.data.total); setCounts(res.data.counts); setPages(res.data.pages);
  }, [page, filters, search]);

  useEffect(() => { load(); }, [load]);

  const formUrl = `${window.location.origin}/demo-request`;

  return (
    <div className="tickets-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Demo Requests</h1>
          <p className="page-subtitle">
            {total} request{total === 1 ? '' : 's'}{counts.new ? ` · ${counts.new} new` : ''}
          </p>
        </div>
        <button className="btn btn-ghost" onClick={() => navigator.clipboard?.writeText(formUrl)} title={formUrl}>
          <Link2 size={14} /> Copy public form link
        </button>
      </div>

      <div className="tickets-toolbar">
        <div className="search-box">
          <Search size={14} />
          <input placeholder="Search company, contact, email, reference..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <div className="filters">
          <Filter size={14} color="var(--text-muted)" />
          <select value={filters.status} onChange={e => { setFilters(f => ({ ...f, status: e.target.value })); setPage(1); }}>
            <option value="">All Statuses</option>
            {DEMO_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}{counts[v] ? ` (${counts[v]})` : ''}</option>)}
          </select>
          <select value={filters.deployment} onChange={e => { setFilters(f => ({ ...f, deployment: e.target.value })); setPage(1); }}>
            <option value="">All Deployments</option>
            {DEPLOYMENTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>

      <div className="ticket-table card">
        <table>
          <thead>
            <tr><th>Reference</th><th>Company</th><th>Contact</th><th>Deployment</th><th>Status</th><th>Submitted</th><th></th></tr>
          </thead>
          <tbody>
            {items.map(d => (
              <tr key={d._id} className="ticket-row" onClick={() => navigate(`/demos/${d._id}`)}>
                <td><span className="ticket-num">{d.reference}</span></td>
                <td><div className="ticket-title">{d.company}</div><div className="ticket-category">{d.product}</div></td>
                <td><div>{d.contactName}</div><div className="ticket-category">{d.email}</div></td>
                <td>{deploymentLabel(d.deployment)}</td>
                <td><span className={`badge ${statusClass(d.status)}`}>{statusLabel(d.status)}</span></td>
                <td><div className="ticket-time">{format(new Date(d.createdAt), 'dd MMM yyyy, HH:mm')}</div></td>
                <td><ChevronRight size={16} color="var(--text-muted)" /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <div className="empty-state">No demo requests found</div>}
      </div>

      {pages > 1 && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16, alignItems: 'center' }}>
          <button className="btn btn-ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
          <span className="text-muted">Page {page} of {pages}</span>
          <button className="btn btn-ghost" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next</button>
        </div>
      )}
    </div>
  );
}
